const MONTH_MAP = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/**
 * Creates a Date object from day, month, year parts
 * @param {string|number} day - The day (1-31)
 * @param {string} month - The month name (Jan, Feb, etc.)
 * @param {string|number} year - The year (e.g., 2025)
 * @returns {Date|null} - Date object or null if invalid
 */
export const createDateFromParts = (day, month, year) => {
  if (!day || !month || !year) {
    return null;
  }

  const monthIndex = MONTH_MAP[month];
  if (monthIndex === undefined) {
    return null;
  }

  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  if (isNaN(dayNum) || isNaN(yearNum)) {
    return null;
  }

  // Create the date
  const date = new Date(yearNum, monthIndex, dayNum);

  // Verify the date is valid (handles invalid dates like Feb 31)
  if (
    date.getFullYear() !== yearNum ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== dayNum
  ) {
    return null;
  }

  return date;
};

/**
 * Validates that a contract start date is at least 21 calendar days from today
 * @param {string|number} day - The day (1-31)
 * @param {string} month - The month name (Jan, Feb, etc.)
 * @param {string|number} year - The year (e.g., 2025)
 * @returns {boolean} - True if the date is valid (21+ days from today), false otherwise
 */
export const validateContractStartDate = (day, month, year) => {
  const selectedDate = createDateFromParts(day, month, year);

  if (!selectedDate) {
    return false;
  }

  // Get today's date at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate the minimum allowed date (today + 21 days)
  const minDate = new Date(today);
  minDate.setDate(today.getDate() + 21);

  // Set selected date to midnight for fair comparison
  selectedDate.setHours(0, 0, 0, 0);

  return selectedDate >= minDate;
};

/**
 * Gets validation error message for invalid contract start dates
 * @param {string|number} day
 * @param {string} month
 * @param {string|number} year
 * @returns {string|null}
 */
export const getDateValidationError = (day, month, year) => {
  // Check if all parts are provided
  if (!day || !month || !year) {
    return "Please select a complete date";
  }

  // Check if the date is valid
  const selectedDate = createDateFromParts(day, month, year);
  if (!selectedDate) {
    return "Please select a valid date";
  }

  // Check if the date meets the 21-day minimum requirement
  if (!validateContractStartDate(day, month, year)) {
    return "The start date must be a minimum 21 calendar days from today";
  }

  return null;
};
